<template>
  <article class="bcard" :class="{ pend: !latest, 'is-evaluating': evaluating }" :aria-busy="evaluating ? 'true' : 'false'">
    <div class="bh">
      <div>
        <div class="bn">{{ brand.brand }}</div>
        <span v-if="overCapacity" class="brand-cap-over">{{ t("brand_cap_over") }}</span>
      </div>
      <button v-if="brand.unseen_alerts" type="button" class="abadge" :aria-label="`${brand.unseen_alerts} ${t('abadge_tip')}`" @click="$emit('history', brand)">{{ brand.unseen_alerts }}</button>
    </div>
    <div class="bc">{{ brand.category || "" }}</div>
    <div v-if="brand.aliases?.length" class="baliases"><span class="alab">{{ t("alias_line") }}:</span> {{ brand.aliases.join(", ") }}</div>
    <div v-if="brand.url" class="baliases"><a v-if="safeUrl" :href="safeUrl" target="_blank" rel="noopener">{{ brand.url }}</a><span v-else>{{ brand.url }}</span><span v-if="safeUrl" class="sr-only"> (opens in new window)</span></div>
    <div v-if="manualQuota" class="bquota" :class="{ exhausted: quotaRemaining === 0 }" role="status">
      {{ t("eval_quota", { used: quotaUsed, limit: quotaLimit }) }}
      <span v-if="quotaRemaining === 0"> · {{ t("eval_quota_done") }}</span>
    </div>
    <template v-if="!latest && (brand.evaluation_pending || busy)">
      <div class="brun"><span class="bspin" aria-hidden="true"></span><div class="brun-in"><div class="brun-h">{{ t("diag_running") }}</div><div class="brun-t">{{ t("diag_running_note") }}<span v-if="minutesRunning >= 1"> · {{ minutesRunning }}{{ t("diag_min") }}</span></div></div></div>
      <div class="sparkghost" aria-hidden="true"><svg viewBox="0 0 280 44" width="100%" preserveAspectRatio="none"><path d="M8 30 C 60 25, 95 34, 138 28 S 225 23, 272 27" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="3 5" stroke-linecap="round" /></svg></div>
      <div class="bmeta"><span>{{ freqLabel }} {{ t("retest_word") }}</span></div>
    </template>
    <template v-else-if="latest">
      <div class="score">
        {{ latest.score ?? 0 }}<small>/100</small>
        <span v-if="latest.band" class="band" :class="String(latest.band || '').toLowerCase()">{{ bandLabel(latest.band) }}</span>
        <span v-if="latest.noise_band" class="nband" :title="t('noise_tip').replace('{n}', latest.noise_band)">± {{ latest.noise_band }}</span>
      </div>
      <svg v-if="sparkPath" class="spark" viewBox="0 0 280 44" width="100%" preserveAspectRatio="none" role="img" :aria-label="trendLabel">
        <path :d="sparkPath.path" fill="none" stroke="#2847C4" stroke-width="2" />
        <circle :cx="sparkPath.x" :cy="sparkPath.y" r="3" fill="#BA3B1D" />
      </svg>
      <div v-else class="sparkghost" aria-hidden="true"><svg viewBox="0 0 280 44" width="100%" preserveAspectRatio="none"><path d="M8 30 C 60 25, 95 34, 138 28 S 225 23, 272 27" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="3 5" stroke-linecap="round" /></svg><span>{{ t("trend_after2") }}</span></div>
      <div class="bstats">
        <div class="bstat"><span class="bsl">{{ t("rec_rate") }}</span><span class="bsv">{{ Math.round(latest.rec_pct || 0) }}%</span></div>
        <div class="bstat"><span class="bsl">{{ t("runs_done") }}</span><span class="bsv">{{ brand.runs_count || 0 }}</span></div>
        <div class="bstat"><span class="bsl">{{ t("checked") }}</span><span class="bsv">{{ ago(brand.last_run) }}</span></div>
        <div class="bstat"><span class="bsl">{{ t("next") }}</span><span class="bsv">{{ until(brand.next_run) }}</span></div>
      </div>
    </template>
    <template v-else-if="!brand.next_run">
      <div class="brun"><span class="bready" aria-hidden="true">•</span><div class="brun-in"><div class="brun-h">{{ t("brand_cap_ready") }}</div><div class="brun-t">{{ t("brand_cap_ready_note") }}</div></div></div>
      <div class="bmeta"><span>{{ freqLabel }} {{ t("retest_word") }}</span></div>
    </template>
    <template v-else-if="minutesRunning >= 20">
      <div class="bstall"><div class="bstall-h"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg>{{ t("diag_stall") }}</div><div class="bstall-t">{{ t("diag_stall_note") }}</div><div class="bstall-a"><button type="button" :disabled="busy" @click="$emit('retest', brand)">{{ t("diag_retry") }}</button></div></div>
    </template>
    <template v-else>
      <div class="brun"><span class="bspin" aria-hidden="true"></span><div class="brun-in"><div class="brun-h">{{ t("diag_running") }}</div><div class="brun-t">{{ t("diag_running_note") }}<span v-if="minutesRunning >= 1"> · {{ minutesRunning }}{{ t("diag_min") }}</span></div></div></div>
      <div class="bmeta"><span>{{ freqLabel }} {{ t("retest_word") }}</span></div>
    </template>

    <!-- GEO_EXEC_R18 · one recommendation, on the card it is about. Off (404) → nothing. -->
    <BrandNextAction v-if="!overCapacity" :brand-id="brand.id" variant="row" />

    <!-- FE-4 · how much of the GEO site is live, and the way into it. -->
    <BrandPagesLine v-if="!overCapacity" :brand-id="brand.id" />

    <div class="bfoot">
      <template v-if="overCapacity">
        <button type="button" class="bremove" @click="$emit('remove', brand, $event)">{{ t("remove") }}</button>
        <button type="button" class="bupgrade" @click="$emit('upgrade', brand)">{{ t("brand_cap_upgrade") }}</button>
      </template>
      <template v-else>
        <button type="button" @click="$emit('history', brand)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 3v18h18M7 14l4-4 3 3 5-6"/></svg>{{ t("history_trend") }}</button>
        <button type="button" @click="$emit('profile', brand)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4Z"/></svg>{{ t("edit_profile") }}</button>
        <button
          v-if="canStartEvaluation"
          type="button"
          :class="{ 'is-running': evaluating }"
          :disabled="retestDisabled"
          :aria-busy="evaluating ? 'true' : undefined"
          :title="retestDisabled && quotaRemaining === 0 ? t('eval_quota_done') : undefined"
          @click="$emit('retest', brand)"
        ><span v-if="evaluating" class="bfoot-spinner" aria-hidden="true"></span><svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 12a9 9 1 1 1-2.64-6.36M21 3v6h-6"/></svg>{{ evaluating ? t("diag_running_retest") : latest ? t("retest_now") : t("rbar_run") }}</button>
      </template>
    </div>
  </article>
</template>

<script setup>
import { computed } from "vue";

import { useI18n } from "../useI18n";
import BrandNextAction from "./BrandNextAction.vue";
import BrandPagesLine from "./BrandPagesLine.vue";  // FE-4

const props = defineProps({ brand: { type: Object, required: true }, overCapacity: Boolean, busy: Boolean });
defineEmits(["history", "profile", "retest", "upgrade", "remove"]);
const { lang, t, bandLabel } = useI18n();
const latest = computed(() => props.brand.latest);
const freqLabel = computed(() => t(`freq_${["manual", "once", "daily", "weekly"].includes(String(props.brand.freq || "").toLowerCase()) ? String(props.brand.freq).toLowerCase() : "weekly"}`));
const manualQuota = computed(() => props.brand.manual_evaluation && props.brand.manual_evaluation.used != null);
const quotaUsed = computed(() => Number(props.brand.manual_evaluation?.used || 0));
const quotaLimit = computed(() => Number(props.brand.manual_evaluation?.limit || 0));
const quotaRemaining = computed(() => Number(props.brand.manual_evaluation?.remaining ?? Math.max(0, quotaLimit.value - quotaUsed.value)));
const evaluating = computed(() => Boolean(props.brand.evaluation_pending || props.busy));
const canStartEvaluation = computed(() => !props.overCapacity && (!latest.value || Boolean(props.brand.manual_evaluation?.enabled)));
const retestDisabled = computed(() => evaluating.value || props.overCapacity || (manualQuota.value && quotaRemaining.value <= 0));
const minutesRunning = computed(() => props.brand.created ? Math.max(0, Math.floor((Date.now() / 1000 - props.brand.created) / 60)) : 0);
const safeUrl = computed(() => /^https?:\/\//i.test(props.brand.url || "") ? props.brand.url : undefined);
const trendLabel = computed(() => `${props.brand.brand}: ${(props.brand.trend || []).join(", ")}`);
const sparkPath = computed(() => {
  const trend = (props.brand.trend || []).map(Number).filter(Number.isFinite);
  if (trend.length < 2) return null;
  const min = Math.min(...trend), range = Math.max(...trend) - min || 1;
  const points = trend.map((value, index) => [8 + index * 264 / (trend.length - 1), 40 - (value - min) / range * 34]);
  return { path: points.map((point, index) => `${index ? "L" : "M"}${point[0].toFixed(1)} ${point[1].toFixed(1)}`).join(" "), x: points.at(-1)[0].toFixed(1), y: points.at(-1)[1].toFixed(1) };
});


function ago(timestamp) {
  if (!timestamp) return "—";
  const seconds = Date.now() / 1000 - timestamp;
  if (seconds < 3600) return `${Math.round(seconds / 60)}${t("ago_m")}`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}${t("ago_h")}`;
  return `${Math.round(seconds / 86400)}${t("ago_d")}`;
}
function until(timestamp) {
  if (!timestamp) return "—";
  const seconds = timestamp - Date.now() / 1000;
  if (seconds < 0) return t("due_now");
  const suffix = lang.value === "zh" ? (seconds < 86400 ? " 小时后" : " 天后") : lang.value === "ja" ? (seconds < 86400 ? "時間後" : "日後") : (seconds < 86400 ? "h" : "d");
  return `${t(seconds < 86400 ? "in_h" : "in_d")}${Math.round(seconds / (seconds < 86400 ? 3600 : 86400))}${suffix}`;
}
</script>
